import { AppStore } from './app/AppStore.ts';
import { Camera } from './render/camera.ts';
import { WorldRenderer } from './render/worldRenderer.ts';
import { PointerController } from './input/pointerController.ts';
import { UIManager } from './ui/uiManager.ts';
import { GAME_CONFIG } from './config/gameConfig.ts';
import { MapPipeline } from './map/mapPipeline.ts';
import { EventEngine } from './simulation/eventEngine.ts';
import { DeterministicRNG } from './simulation/rng.ts';
import { GhostClient } from './multiplayer/ghostClient.ts';
import { NarrativeClient } from './ai/narrator.ts';
import { CustomerAggregateManager } from './simulation/customerAggregate.ts';
import { TrendsService } from './services/trendsService.ts';
import { AudioService } from './audio/audioService.ts';

declare global {
  interface Window {
    __gameStore?: AppStore;
    __gameCamera?: Camera;
    __gameUIManager?: UIManager;
    __gameAudioService?: AudioService;
  }
}

function bootstrap(): void {
  const appContainer = document.getElementById('app');
  if (!appContainer) {
    throw new Error('Root #app container not found');
  }

  // 1. Setup Canvas
  const canvas = document.createElement('canvas');
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';
  appContainer.appendChild(canvas);

  // 2. Core services
  const store = new AppStore();
  const camera = new Camera();
  const renderer = new WorldRenderer(canvas, camera);
  const uiManager = new UIManager(appContainer, store, camera);
  const audioService = AudioService.getInstance();
  audioService.init();

  window.__gameStore = store;
  window.__gameCamera = camera;
  window.__gameUIManager = uiManager;
  window.__gameAudioService = audioService;
  const rng = new DeterministicRNG(store.getState().seed);
  const eventEngine = new EventEngine(rng);
  const narrativeClient = new NarrativeClient();
  const trendsService = new TrendsService();

  trendsService.fetchLiveTrends().then((topics) => {
    const items = trendsService.getDetailedItems();
    if (items.length > 0) {
      store.dispatch({ type: 'SET_TRENDING_ITEMS', items });
    }
    const current = store.getState().currentTrendingTopic;
    if (!current && topics.length > 0 && topics[0]) {
      store.dispatch({ type: 'SET_TRENDING_TOPIC', topic: topics[0] });
    }
  });

  const loadNeighborhood = async (
    lat: number,
    lon: number,
    label: string,
    allowProcedural = false
  ): Promise<void> => {
    store.dispatch({
      type: 'SET_LOADING_MESSAGE',
      message: `🛰️ Đang kết nối vệ tinh và tải bản đồ thực tế "${label.split(',')[0]}"...`,
    });
    try {
      const neighborhood = await MapPipeline.buildNeighborhood({ lat, lon }, label, allowProcedural);
      store.dispatch({ type: 'SET_NEIGHBORHOOD', neighborhood });
      camera.resetZoom();

      // Tải ghost stalls cho vị trí mới
      const newGhosts = await GhostClient.queryNearbyGhosts({ lat, lon });
      store.dispatch({ type: 'SET_GHOSTS', ghosts: newGhosts });
    } catch (err: unknown) {
      console.warn('[main.ts] Live OSM fetch failed:', err);
      uiManager.showMapLoadError(label, async (action) => {
        if (action === 'retry') {
          await loadNeighborhood(lat, lon, label, false);
        } else if (action === 'procedural') {
          await loadNeighborhood(lat, lon, label, true);
        } else if (action === 'repick') {
          uiManager.showLocationPicker(async (newLat, newLon, newLabel) => {
            await loadNeighborhood(newLat, newLon, newLabel, false);
          });
        }
      });
    } finally {
      store.dispatch({ type: 'SET_LOADING_MESSAGE', message: null });
    }
  };

  uiManager.setNeighborhoodLoader(loadNeighborhood);

  // 3. Pointer & Interaction
  new PointerController(canvas, camera, store, () => store.getState());

  // 4. Resize handling (DPI safe)
  const handleResize = (): void => {
    renderer.resize();
  };
  window.addEventListener('resize', handleResize);
  handleResize();

  // 5. UI Subscription
  store.subscribe((state) => {
    uiManager.render(state);
  });

  // 6. Tải Ghost Stalls
  const initialCenter = store.getState().neighborhood?.center ?? { lat: 21.0135, lon: 105.8504 };
  GhostClient.queryNearbyGhosts(initialCenter).then((ghosts) => {
    store.dispatch({ type: 'SET_GHOSTS', ghosts });
  });

  // 7. Event & Narrative Tick (mỗi 45s) — Chỉ chạy khi người chơi ĐÃ MỞ QUÁN & không pause / modal
  window.setInterval(async () => {
    const currentState = store.getState();
    if (
      currentState.phase !== 'PLAYING' ||
      currentState.isPaused ||
      uiManager.isAnyModalOpen() ||
      currentState.player.ownedStalls.length === 0
    ) {
      return;
    }

    const event = eventEngine.checkTriggers(currentState);
    if (event !== currentState.activeEvent) {
      store.dispatch({ type: 'SET_ACTIVE_EVENT', activeEvent: event });
    }

    // AI narrative async bên ngoài simulation tick
    if (currentState.neighborhood && Math.random() < 0.25) {
      const quote = await narrativeClient.getContextLore(
        'bán đắt hàng',
        currentState.neighborhood.label,
        currentState.neighborhood.center
      );
      store.dispatch({ type: 'SET_NARRATIVE', quote });
    }
  }, 45_000);

  // 7b. Google Trends VN Topic Rotation (Mỗi 60s bà trà đá buôn chuyện hot trend)
  window.setInterval(() => {
    const s = store.getState();
    if (
      s.phase !== 'PLAYING' ||
      s.isPaused ||
      uiManager.isAnyModalOpen() ||
      s.player.ownedStalls.length === 0
    ) {
      return;
    }
    const nextTopic = trendsService.rotateTopic();
    store.dispatch({ type: 'SET_TRENDING_TOPIC', topic: nextTopic });
  }, 60_000);

  // 7c. Google Trends Auto-Refresh (Mỗi 30 phút tự động đồng bộ trend mới nhất từ server)
  window.setInterval(() => {
    trendsService
      .fetchLiveTrends(true)
      .then(() => {
        const items = trendsService.getDetailedItems();
        if (items.length > 0) {
          store.dispatch({ type: 'SET_TRENDING_ITEMS', items });
        }
      })
      .catch(() => {});
  }, 30 * 60 * 1000);

  // 8. Fixed Simulation Tick (250ms)
  const customerManager = new CustomerAggregateManager();

  window.setInterval(() => {
    const state = store.getState();
    if (state.phase === 'LOBBY' || state.phase === 'PAUSED' || state.isPaused || uiManager.isAnyModalOpen()) {
      return;
    }

    const roads = state.neighborhood?.roads ?? [];
    const ownedLocations = state.player.ownedStalls.map((sid) => {
      const stall = state.stalls[sid];
      const node = state.neighborhood?.nodes.find((n) => n.id === stall?.nodeId);
      return { id: sid, x: node?.x ?? 0, y: node?.y ?? 0, chairs: stall?.chairs ?? 4 };
    });

    const rivalLocations = Object.entries(state.dummyRivals ?? {}).map(([nodeId, r]) => {
      const node = state.neighborhood?.nodes.find((n) => n.id === nodeId);
      return { id: r.id, x: node?.x ?? 0, y: node?.y ?? 0, name: r.name, status: r.status };
    });

    const activeCustomers = customerManager.update(
      GAME_CONFIG.traffic.tickMs,
      roads,
      ownedLocations,
      state.player.unlockedPerks ?? [],
      state.currentShift,
      state.weather?.type ?? 'sunny',
      state.currentTrendingTopic,
      rivalLocations
    );

    store.dispatch({
      type: 'TICK',
      deltaMs: GAME_CONFIG.traffic.tickMs,
      customers: activeCustomers,
    });
  }, GAME_CONFIG.traffic.tickMs);

  // 9. Dedicated Render Loop (requestAnimationFrame)
  const renderLoop = (): void => {
    renderer.render(store.getState());
    requestAnimationFrame(renderLoop);
  };
  requestAnimationFrame(renderLoop);

  // 10. Location Picker Event Listener
  const originalDispatch = store.dispatch.bind(store);
  store.dispatch = (cmd) => {
    if (cmd.type === 'SELECT_LOCATION' && cmd.lat === 0 && cmd.lon === 0) {
      uiManager.showLocationPicker(async (lat, lon, label) => {
        await loadNeighborhood(lat, lon, label);
      });
      return;
    }
    originalDispatch(cmd);
  };

  // 11. Auto Advance Day Cron (Mỗi 5 phút thực = 300_000 ms nếu người chơi đã có quán)
  window.setInterval(() => {
    const s = store.getState();
    if (s.phase !== 'PLAYING' || s.isPaused || uiManager.isAnyModalOpen() || s.player.ownedStalls.length === 0) return;
    if (s.showDayReport) return; // đang xem report
    store.dispatch({ type: 'ADVANCE_DAY' });
  }, 300_000);

  // 12. Visibility change handling: Tạm dừng khi chuyển tab / ẩn màn hình
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      const s = store.getState();
      if (s.phase === 'PLAYING') {
        store.dispatch({ type: 'PAUSE_GAME' });
        audioService.pauseMusic();
      }
    } else if (document.visibilityState === 'visible') {
      const s = store.getState();
      if (s.phase === 'PAUSED' || s.isPaused) {
        uiManager.openPauseMenu(s);
      }
    }
  });

  console.log('[BaChuViaHe 2.0] Full pipeline active: GPS, OSM, Events, Territory, Ghosts, AI!');
}

window.addEventListener('DOMContentLoaded', bootstrap);
